import React from "react";
import { useTranslation } from "react-i18next";

import { BadgeStatus } from "@/types/general";
import { cn } from "@helpers/utils";
import { getOrderStatusIcon } from "@reusables/BaseOrderStatusBadge";

type OrderStatusInlineFilterProps = {
    options: Record<BadgeStatus.Base, boolean>;
    selectedValues: BadgeStatus.Base[];
    onChange: (next: BadgeStatus.Base[]) => void;
    title?: string;
    className?: string;
};

const statusRenderOrder: BadgeStatus.Base[] = [
    BadgeStatus.Base.offer,
    BadgeStatus.Base.new,
    BadgeStatus.Base.in_progress,
    BadgeStatus.Base.completed,
    BadgeStatus.Base.cancelled,
];

function getChipClassName(active: boolean): string {
    return cn(
        "inline-flex items-center gap-[4px] h-[32px] px-[10px] rounded-[8px] border border-solid text-[14px] leading-[1.4] transition-colors",
        active
            ? "bg-[#7556FA] !border-[#7556FA] text-white [&_svg]:!text-white [&_svg]:!stroke-white [&_svg_*]:!stroke-white"
            : "bg-white !border-[#E5E5E5] text-[#515151]"
    );
}

export default function OrderStatusInlineFilter({
    options,
    selectedValues,
    onChange,
    title,
    className,
}: OrderStatusInlineFilterProps) {
    const { t } = useTranslation();
    const { t: statusesT } = useTranslation("", { keyPrefix: "general.orders.statuses.base" });

    const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);
    const visibleStatuses = React.useMemo(
        () => statusRenderOrder.filter((status) => options[status]),
        [options]
    );

    const toggleStatus = (status: BadgeStatus.Base) => {
        if (selectedSet.has(status)) {
            onChange(selectedValues.filter((item) => item !== status));
            return;
        }

        onChange([...selectedValues, status]);
    };

    return (
        <div className={cn("bg-[#F9F9F9] rounded-[8px] px-[16px] py-[8px]", className)}>
            <div
                className="flex flex-wrap items-center gap-[12px]"
                role="group"
                aria-label={title ?? t("general.dropdownAll")}
            >
                {title ? (
                    <div className="font-semibold text-[14px] leading-[1.5] text-[#515151]">{title}</div>
                ) : null}

                <div className="flex flex-wrap items-center gap-[8px]">
                    <button
                        type="button"
                        aria-pressed={selectedValues.length === 0}
                        className={getChipClassName(selectedValues.length === 0)}
                        onClick={() => onChange([])}
                    >
                        {t("general.dropdownAll")}
                    </button>

                    {visibleStatuses.map((status) => (
                        <button
                            key={status}
                            type="button"
                            aria-pressed={selectedSet.has(status)}
                            className={getChipClassName(selectedSet.has(status))}
                            onClick={() => toggleStatus(status)}
                        >
                            {getOrderStatusIcon(status)}
                            <span>{statusesT(status)}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
